import React, { useState, useEffect, useCallback } from "react";
import { Search, Loader2, X, Save, RotateCcw, Filter } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/**
 * Search & Edit texts across the entire app.
 *
 * Data flow:
 *   1. Admin types a query → POST /api/i18n/search with { q, sources? }.
 *   2. Backend walks the i18n dictionary + Mongo documents and returns
 *      matches with { source, label, values: {pt,en,es}, endpoint, ... }.
 *   3. Clicking Edit opens the panel with three language inputs.
 *   4. Save routes to the right endpoint (i18n override, homepage-config,
 *      eliminatoria, tier, etc.) using the metadata the backend already
 *      supplied — no client-side mapping table.
 */

const SOURCE_LABELS = {
  i18n: "Dicionário",
  homepage_config: "Homepage",
  eliminatorias: "Eliminatórias",
  tiers: "Escalões",
  robots: "Robots",
  competitions: "Competições",
  games: "Jogos",
  game_results_settings: "Resultados",
};

const SOURCE_COLORS = {
  i18n: "bg-sky-100 text-sky-800",
  homepage_config: "bg-violet-100 text-violet-800",
  eliminatorias: "bg-amber-100 text-amber-800",
  tiers: "bg-emerald-100 text-emerald-800",
  robots: "bg-cyan-100 text-cyan-800",
  competitions: "bg-rose-100 text-rose-800",
  games: "bg-indigo-100 text-indigo-800",
  game_results_settings: "bg-slate-200 text-slate-800",
};

export default function SearchAndEdit() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [capped, setCapped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [enabledSources, setEnabledSources] = useState(() => new Set(Object.keys(SOURCE_LABELS)));

  const runSearch = useCallback(async () => {
    if (!q.trim()) {
      setResults([]); setCount(0); setCapped(false);
      return;
    }
    setLoading(true);
    try {
      const sources = enabledSources.size === Object.keys(SOURCE_LABELS).length ? null : [...enabledSources];
      const { data } = await api.post("/i18n/search", { q: q.trim(), sources });
      setResults(data.results || []);
      setCount(data.count || 0);
      setCapped(!!data.capped);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro na pesquisa.");
    } finally {
      setLoading(false);
    }
  }, [q, enabledSources]);

  // Debounced auto-search on typing.
  useEffect(() => {
    const id = setTimeout(runSearch, 350);
    return () => clearTimeout(id);
  }, [runSearch]);

  const toggleSource = (src) => {
    setEnabledSources((prev) => {
      const next = new Set(prev);
      if (next.has(src)) next.delete(src);
      else next.add(src);
      return next;
    });
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="admin-search-edit">
      <header>
        <h1 className="font-[Fredoka] text-3xl font-bold text-slate-900 flex items-center gap-2">
          <Search className="w-7 h-7 text-sky-500" /> Pesquisar & Editar textos
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Encontra qualquer texto visível na app (botões, títulos, mensagens…) e edita-o nas 3 línguas.
          Alterações ficam disponíveis imediatamente sem deploy.
        </p>
      </header>

      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              autoFocus
              placeholder='Ex: "rumo à grande", "entrar", "SUPERTMATIK"…'
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              className="pl-10 pr-10 h-11 text-base"
              data-testid="search-input"
            />
            {q && (
              <button
                onClick={() => setQ("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                data-testid="search-clear"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {loading && <Loader2 className="w-5 h-5 text-sky-500 animate-spin" />}
        </div>

        {/* Source filter chips */}
        <div className="flex items-center gap-1.5 mt-3 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-slate-400 mr-1" />
          {Object.entries(SOURCE_LABELS).map(([src, label]) => (
            <button
              key={src}
              type="button"
              onClick={() => toggleSource(src)}
              className={`text-[11px] font-bold px-2.5 py-1 rounded-full transition-colors ${
                enabledSources.has(src) ? SOURCE_COLORS[src] : "bg-slate-100 text-slate-400 line-through"
              }`}
              data-testid={`search-filter-${src}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {q.trim() && (
        <div className="text-xs text-slate-500 px-1">
          {loading ? "A pesquisar…" : `${count} resultado${count === 1 ? "" : "s"}${capped ? " (a mostrar os 200 primeiros)" : ""}`}
        </div>
      )}

      <div className="space-y-2" data-testid="search-results">
        {results.map((r, i) => (
          <ResultRow key={`${r.source}-${r.label}-${i}`} row={r} onEdit={() => setSelected(r)} query={q.trim()} />
        ))}
        {!loading && q.trim() && results.length === 0 && (
          <div className="text-center text-slate-400 py-16 border-2 border-dashed border-slate-200 rounded-2xl">
            Nenhum resultado. Tenta outra palavra ou activa mais fontes acima.
          </div>
        )}
      </div>

      {selected && (
        <EditPanel
          row={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            runSearch();
          }}
        />
      )}
    </div>
  );
}

function highlight(text, q) {
  // Defensive: only strings are searchable. Non-string (React nodes or
  // undefined) short-circuits to a safe passthrough so the search UI
  // never crashes when a language value is empty.
  if (!q || typeof text !== "string") return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-200 text-slate-900 rounded px-0.5">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}

function ResultRow({ row, onEdit, query }) {
  const values = row.values || (row.value ? { pt: row.value, en: "", es: "" } : { pt: "", en: "", es: "" });
  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-3 hover:border-sky-300 transition-colors"
      data-testid={`search-row-${row.source}-${row.label}`}
    >
      <div className="flex items-start gap-3">
        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full shrink-0 ${SOURCE_COLORS[row.source] || "bg-slate-100"}`}>
          {SOURCE_LABELS[row.source] || row.source}
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-xs text-slate-500 truncate" title={row.label}>
            {row.label}
            {row.is_overridden && (
              <span className="ml-2 text-[10px] font-bold text-emerald-600 uppercase tracking-wider">customizado</span>
            )}
          </div>
          <div className="mt-1 grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
            {["pt", "en", "es"].map((lang) => {
              const val = values[lang] || "";
              return (
                <div key={lang} className="min-w-0">
                  <div className="text-[9px] font-bold text-slate-400 uppercase">{lang}</div>
                  <div className="text-slate-800 truncate" title={val}>
                    {val ? highlight(val, query) : <span className="text-slate-300 italic">—</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <Button
          size="sm"
          onClick={onEdit}
          className="shrink-0 bg-sky-500 hover:bg-sky-600 text-white rounded-full font-bold h-8 px-3"
          data-testid={`search-edit-btn-${row.source}-${row.label}`}
        >
          Editar
        </Button>
      </div>
    </div>
  );
}

function EditPanel({ row, onClose, onSaved }) {
  const [values, setValues] = useState({ pt: "", en: "", es: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValues({
      pt: row.values?.pt || row.value || "",
      en: row.values?.en || "",
      es: row.values?.es || "",
    });
  }, [row]);

  const save = async () => {
    setSaving(true);
    try {
      // Route by source. Every row already carries the endpoint hint from
      // the backend — we just wire the payload to the corresponding shape.
      if (row.source === "i18n") {
        await api.put(`/i18n/overrides/${encodeURIComponent(row.key)}`, values);
      } else if (row.source === "homepage_config") {
        // Path is like "hero.title" (grouped) → we save
        // hero.title_pt / _en / _es. Or a single-language leaf path.
        const patch = buildDeepPatch(row.path, values, row.values);
        await api.put("/homepage-config", patch);
      } else if (row.source === "eliminatorias") {
        await saveTranslated("/eliminatorias", row.doc_id, row.path, values);
      } else if (row.source === "tiers") {
        await saveTranslated("/tiers", row.doc_id, row.path, values);
      } else if (row.source === "robots") {
        await saveTranslated("/robots", row.doc_id, row.path, values);
      } else if (row.source === "games") {
        await saveTranslated("/games", row.doc_id, row.path, values);
      } else if (row.source === "competitions") {
        await saveTranslated("/competitions", row.doc_id, row.path, values);
      } else if (row.source === "game_results_settings") {
        await api.put(`/results-settings/${row.doc_id}`, {
          [`${row.path}_pt`]: values.pt,
          [`${row.path}_en`]: values.en,
          [`${row.path}_es`]: values.es,
        });
      } else {
        toast.error(`Fonte "${row.source}" ainda não suportada — edita via o separador dedicado.`);
        return;
      }
      toast.success("Texto guardado! As alterações estão activas em todo o site.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro a guardar.");
    } finally {
      setSaving(false);
    }
  };

  const revert = async () => {
    if (row.source !== "i18n") {
      toast.info("Só chaves i18n podem ser revertidas ao valor original.");
      return;
    }
    setSaving(true);
    try {
      await api.delete(`/i18n/overrides/${encodeURIComponent(row.key)}`);
      toast.success("Override removido — voltou ao valor original.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro a reverter.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[300] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-panel"
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
              {SOURCE_LABELS[row.source] || row.source}
            </div>
            <h3 className="font-[Fredoka] text-lg font-bold text-slate-900 truncate" title={row.label}>
              {row.label}
            </h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" data-testid="edit-panel-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          {["pt", "en", "es"].map((lang) => (
            <div key={lang}>
              <Label className="text-xs font-bold uppercase text-slate-500 mb-1 flex items-center gap-2">
                {lang === "pt" ? "🇵🇹 Português" : lang === "en" ? "🇬🇧 English" : "🇪🇸 Español"}
              </Label>
              {((values[lang] || "").length > 60) ? (
                <Textarea
                  value={values[lang]}
                  onChange={(e) => setValues((v) => ({ ...v, [lang]: e.target.value }))}
                  rows={3}
                  className="font-normal"
                  data-testid={`edit-panel-${lang}`}
                />
              ) : (
                <Input
                  value={values[lang]}
                  onChange={(e) => setValues((v) => ({ ...v, [lang]: e.target.value }))}
                  data-testid={`edit-panel-${lang}`}
                />
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between gap-2 mt-6 pt-4 border-t border-slate-100">
          {row.source === "i18n" && row.is_overridden ? (
            <Button variant="ghost" onClick={revert} disabled={saving} className="text-slate-500 hover:text-rose-600" data-testid="edit-panel-revert">
              <RotateCcw className="w-4 h-4 mr-1" /> Reverter ao original
            </Button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="edit-panel-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Guardar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Helpers ---------------------------------------------------------------
async function saveTranslated(endpointPrefix, docId, field, values) {
  // Canonical field holds PT, translations.{en|es}.{field} hold the others.
  await api.put(`${endpointPrefix}/${docId}`, {
    [field]: values.pt,
    translations: {
      en: { [field]: values.en },
      es: { [field]: values.es },
    },
  });
}

function buildDeepPatch(dottedPath, values, prevValues) {
  // Given "hero.title" and { pt, en, es } produce:
  //   { hero: { title_pt: "...", title_en: "...", title_es: "..." } }
  // If path was already a full leaf (like "hero.title_pt" — a non-triplet),
  // save only that language.
  const isTriplet = !!prevValues;
  const parts = dottedPath.split(".");
  const leaf = parts.pop();
  const root = {};
  let cur = root;
  for (const p of parts) {
    cur[p] = {};
    cur = cur[p];
  }
  if (isTriplet) {
    cur[`${leaf}_pt`] = values.pt;
    cur[`${leaf}_en`] = values.en;
    cur[`${leaf}_es`] = values.es;
  } else {
    // Single leaf path — apply the PT value (the only one shown).
    cur[leaf] = values.pt;
  }
  return root;
}
